import { Divider, Stack, Typography } from '@mui/material';
import { DynamicIcon, Tooltip } from 'src/components';
import { TOOLTIP_PAPER_STYLE_SLOT_PROPS } from 'src/components/Tooltip/tooltip-paper-style-slot-props';
import { Address } from 'src/graphql';

function getAddressTooltipLines(address: Address): { id: string; text: string }[] {
  const businessName = address.businessName?.trim();
  const address1 = address.address1?.trim();
  const address2 = address.address2?.trim();
  const city = address.city?.trim();
  const state = address.state?.trim();
  const zip = address.zip?.trim();

  const raw: string[] = [];
  if (businessName) raw.push(businessName);
  if (address1 || address2) raw.push([address1, address2].filter(Boolean).join(' '));
  if (city || state || zip) {
    const cityState = [city, [state, zip].filter(Boolean).join(' ')].filter(Boolean).join(', ');
    if (cityState) raw.push(cityState);
  }
  if (raw.length === 0 && address?.fullAddress?.trim()) {
    raw.push(address.fullAddress.trim());
  }
  return raw.map((text, i) => ({ id: `address-line-${i}`, text }));
}

export const SiteAddress = ({
  address,
  onClick,
  tooltipText,
}: {
  address: Address;
  onClick?: (event: React.MouseEvent<HTMLElement>) => void;
  tooltipText?: string | ((children: React.ReactNode) => React.ReactNode);
}) => {
  let tooltipTitle: React.ReactNode | undefined;

  if (onClick) {
    if (typeof tooltipText === 'function') {
      tooltipTitle = tooltipText(address?.fullAddress);
    } else if (tooltipText !== undefined) {
      const lines = getAddressTooltipLines(address);
      tooltipTitle = (
        <Stack direction="column" spacing={1} p={1}>
          {lines.map(({ id, text }) => (
            <Typography key={id} variant="body2">
              {text}
            </Typography>
          ))}
          {lines.length > 0 && <Divider />}
          <Typography variant="body2" color="text.secondary">
            {tooltipText}
          </Typography>
        </Stack>
      );
    } else {
      const lines = getAddressTooltipLines(address);
      tooltipTitle =
        lines.length > 0
          ? lines.map(({ id, text }) => (
              <Typography key={id} variant="body2">
                {text}
              </Typography>
            ))
          : undefined;
    }
  }

  return (
    <>
      <Tooltip title={address?.brand?.name} hideWhenRefIsHidden>
        <DynamicIcon
          name={address?.brand?.name.toLowerCase().includes('commercial') ? 'domain' : 'home'}
          weight={500}
          filled
          fontSize="1.25rem"
          sx={{ cursor: 'default' }}
        />
      </Tooltip>
      <Tooltip hideWhenRefIsHidden slotProps={TOOLTIP_PAPER_STYLE_SLOT_PROPS} title={tooltipTitle}>
        <Typography
          variant="subtitle2"
          paddingBlock={1}
          textOverflow="ellipsis"
          whiteSpace="nowrap"
          overflow="hidden"
          sx={onClick ? { cursor: 'pointer' } : undefined}
          onClick={onClick}
        >
          {address?.fullAddress}
        </Typography>
      </Tooltip>
    </>
  );
};
